import type { components } from '@heidi/schema/ml-scribe';
import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { injected } from '../injected-auth';
type SettingsCollectionResponse = components['schemas']['SettingsCollectionResponse'];
export interface WorkHomeModeSettingsPort {
  getResolvedSettings: (keys: string[]) => Promise<{ data: SettingsCollectionResponse }>;
  patchSettings: (
    values: Record<string, unknown>
  ) => Promise<{ data: SettingsCollectionResponse }>;
}

// mirrors link_share-default_* hooks until Work-home settings carve out.
export const WORK_HOME_MODE_SETTING_KEY = 'work-home_mode';
export const GET_WORK_HOME_MODE_KEY = 'getWorkHomeModeSetting';

/** Last resolved mode from the query cache, when a prior visit already fetched it. */
export function readCachedWorkHomeMode(
  queryClient: QueryClient
): PersistedWorkHomeMode | null {
  const cached = queryClient.getQueryData<PersistedWorkHomeMode | null>([
    GET_WORK_HOME_MODE_KEY,
  ]);
  if (cached === 'work' || cached === 'evidence') {
    return cached;
  }
  return null;
}

/** Values persisted by ml-scribe's settings catalog for Work home page mode. */
export type PersistedWorkHomeMode = 'work' | 'evidence';

function toPersistedMode(data: SettingsCollectionResponse): PersistedWorkHomeMode | null {
  const value = data.settings[WORK_HOME_MODE_SETTING_KEY]?.value;
  if (value === 'work' || value === 'evidence') {
    return value;
  }
  return null;
}

/** The app's settings pair, wired once through `configureAgentsData`. */
const workHomeModeSettings = (): WorkHomeModeSettingsPort =>
  injected('workHomeModeSettings');

/**
 * Resolved Work home page mode from the layered settings API.
 * API: GET /api/v2/ml-scribe/settings?keys=work-home_mode
 */
export function useQueryWorkHomeMode(enabled: boolean) {
  const { getResolvedSettings } = workHomeModeSettings();
  return useQuery<PersistedWorkHomeMode | null, Error>({
    queryKey: [GET_WORK_HOME_MODE_KEY],
    enabled,
    queryFn: async () => {
      const { data } = await getResolvedSettings([WORK_HOME_MODE_SETTING_KEY]);
      return toPersistedMode(data);
    },
  });
}

/**
 * Persist Work home page mode at the user's home layer.
 * API: PATCH /api/v2/ml-scribe/settings
 */
export function useMutateWorkHomeMode() {
  const { patchSettings } = workHomeModeSettings();
  const queryClient = useQueryClient();

  return useMutation<PersistedWorkHomeMode | null, Error, PersistedWorkHomeMode>({
    mutationFn: async (mode) => {
      const { data } = await patchSettings({
        [WORK_HOME_MODE_SETTING_KEY]: mode,
      });
      return toPersistedMode(data);
    },
    onSuccess: (mode) => {
      queryClient.setQueryData([GET_WORK_HOME_MODE_KEY], mode);
    },
  });
}
